import type { LucideIcon } from "lucide-react";
import {
  AppWindow,
  ArrowUpFromLine,
  BarChart3,
  Blocks,
  Bot,
  BotMessageSquare,
  Boxes,
  Cpu,
  Database,
  FileText,
  Flag,
  Gauge,
  GitBranch,
  PanelsTopLeft,
  KeyRound,
  MessageSquareWarning,
  Landmark,
  LayoutDashboard,
  LayoutGrid,
  Library,
  Package,
  PlugZap,
  Rocket,
  ScrollText,
  Settings2,
  ShieldCheck,
  Sparkles,
  Workflow,
  Wrench,
  Zap,
} from "lucide-react";

/** Permission keys mirror `Ncp.Application/Platform/Permissions.cs`. */
export const P = {
  overview: "platform.overview.read",
  services: "platform.services.read",
  forms: "platform.forms.read",
  workflows: "platform.workflows.read",
  rules: "platform.rules.read",
  automations: "platform.automations.read",
  workspaces: "platform.workspaces.read",
  assistants: "platform.assistants.read",
  knowledge: "platform.knowledge.read",
  agents: "platform.agents.read",
  models: "platform.models.read",
  governance: "platform.governance.read",
  integrations: "platform.integrations.read",
  identity: "platform.identity.read",
  content: "platform.content.read",
  settings: "platform.settings.read",
  releases: "platform.releases.read",
  packages: "platform.packages.read",
  audit: "platform.audit.read",
  /** The reported issues and their classification, without the audit trail (condition 1.7): every role that classifies a defect holds it. */
  issues: "issues.classify",
  data: "platform.recordtypes.read",
  reports: "platform.reports.read",
  screens: "platform.screens.read",
  tools: "platform.tools.read",
  environments: "platform.environments.read",
  pages: "pages.author",
} as const;

export interface ConsoleNavItem {
  key: string;
  /** i18n key under `admin:nav`. */
  label: string;
  icon: LucideIcon;
  href: string;
  permission: string;
  /** Other permissions any one of which also opens the page. */
  orPermissions?: string[];
  /** Marks AI-assisted areas in the sidebar. */
  ai?: boolean;
  /** Single-letter shortcut used after `g` (e.g. `g s` → services). */
  shortcut?: string;
  /** No console page yet: kept in the map but left out of the sidebar, palette and shortcuts. */
  planned?: boolean;
}

export interface ConsoleNavSection {
  /** i18n key under `admin:nav`, omitted for the top group. */
  title?: string;
  items: ConsoleNavItem[];
}

const NAV_MAP: ConsoleNavSection[] = [
  {
    items: [{ key: "overview", label: "overview", icon: Gauge, href: "/", permission: P.overview, shortcut: "o" }],
  },
  {
    title: "build",
    items: [
      { key: "services", label: "services", icon: LayoutGrid, href: "/services", permission: P.services, shortcut: "s" },
      { key: "data", label: "data", icon: Database, href: "/data", permission: P.data, shortcut: "e" },
      { key: "forms", label: "forms", icon: FileText, href: "/forms", permission: P.forms, shortcut: "f" },
      { key: "workflows", label: "workflows", icon: Workflow, href: "/workflows", permission: P.workflows, shortcut: "w" },
      { key: "rules", label: "rules", icon: GitBranch, href: "/rules", permission: P.rules, shortcut: "u" },
      { key: "reports", label: "reports", icon: BarChart3, href: "/reports", permission: P.reports, shortcut: "q" },
      { key: "screens", label: "screens", icon: PanelsTopLeft, href: "/screens", permission: P.screens, shortcut: "n" },
      { key: "automations", label: "automations", icon: Zap, href: "/automations", permission: P.automations, shortcut: "a" },
    ],
  },
  {
    title: "aiWorkspace",
    items: [
      { key: "workspaces", label: "workspaces", icon: Blocks, href: "/workspaces", permission: P.workspaces, shortcut: "h" },
      { key: "pages", label: "pages", icon: LayoutDashboard, href: "/pages", permission: P.pages, ai: true, shortcut: "c" },
      { key: "assistants", label: "assistants", icon: BotMessageSquare, href: "/assistants", permission: P.assistants, ai: true, shortcut: "b" },
      { key: "knowledge", label: "knowledge", icon: Library, href: "/knowledge", permission: P.knowledge, shortcut: "k" },
      { key: "solutions", label: "solutions", icon: AppWindow, href: "/solutions", permission: P.workspaces, shortcut: "l" },
    ],
  },
  {
    title: "ai",
    items: [
      { key: "agents", label: "agents", icon: Bot, href: "/agents", permission: P.agents, ai: true, shortcut: "g" },
      { key: "tools", label: "tools", icon: Wrench, href: "/tools", permission: P.tools, shortcut: "j" },
      { key: "models", label: "models", icon: Cpu, href: "/models", permission: P.models, shortcut: "m", planned: true },
      { key: "governance", label: "governance", icon: Sparkles, href: "/governance", permission: P.governance, ai: true, shortcut: "v" },
    ],
  },
  {
    title: "connect",
    items: [
      { key: "integrations", label: "integrations", icon: PlugZap, href: "/integrations", permission: P.integrations, shortcut: "i" },
      { key: "identity", label: "identity", icon: KeyRound, href: "/identity", permission: P.identity, shortcut: "d" },
      { key: "content", label: "content", icon: ScrollText, href: "/content", permission: P.content, shortcut: "t", planned: true },
    ],
  },
  {
    title: "operate",
    items: [
      { key: "settings", label: "settings", icon: Settings2, href: "/settings", permission: P.settings, shortcut: "," },
      { key: "releases", label: "releases", icon: Rocket, href: "/releases", permission: P.releases, shortcut: "r" },
      { key: "packages", label: "packages", icon: Package, href: "/packages", permission: P.packages, shortcut: "p" },
      { key: "environments", label: "environments", icon: ArrowUpFromLine, href: "/environments", permission: P.environments, shortcut: "y" },
      { key: "audit", label: "audit", icon: ShieldCheck, href: "/audit", permission: P.audit, shortcut: "x" },
      { key: "feedback", label: "feedback", icon: MessageSquareWarning, href: "/feedback", permission: P.audit, orPermissions: [P.issues] },
    ],
  },
];

export const CONSOLE_NAV: ConsoleNavSection[] = NAV_MAP.map((s) => ({ ...s, items: s.items.filter((i) => !i.planned) })).filter((s) => s.items.length > 0);

export const ALL_NAV_ITEMS: ConsoleNavItem[] = CONSOLE_NAV.flatMap((s) => s.items);

/** Whether the signed-in person may open a console page: its permission, or any of the others that also open it. */
export function navAllowed(item: ConsoleNavItem, can: (permission: string) => boolean): boolean {
  return can(item.permission) || (item.orPermissions ?? []).some(can);
}

// Icons referenced by pages for consistency with the sidebar.
export const NAV_ICONS = { Boxes, Flag, Landmark };
